/* site/content/SiteContent.jsx — the site's content layer. Every page ships
 * its own defaults (the prototype's inline data, extracted); the superadmin
 * "Edit site" section stores overrides in Mongo which GET /api/site/content
 * returns and this provider deep-merges over the defaults. So the public
 * site always renders — with or without the backend — and every text/image
 * is editable without a code change (ARCHITECTURE.md rule 7). */
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useEditSession } from "../edit/EditContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const Ctx = createContext({ content: {}, loaded: false, cms: null });

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
/** overrides win; objects merge key-by-key; arrays and scalars replace */
export function mergeContent(base, over) {
  if (over === undefined || over === null) return base;
  if (isObj(base) && isObj(over)) {
    const out = { ...base };
    Object.keys(over).forEach((k) => { out[k] = mergeContent(base[k], over[k]); });
    return out;
  }
  return over;
}

export function SiteContentProvider({ children, initial = null }) {
  const [state, setState] = useState({ content: initial || {}, loaded: !!initial, cms: null });
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/site/content`, { headers: { Authorization: undefined } })
      .then((r) => { if (alive && r.data && isObj(r.data)) setState((s) => ({ ...s, content: r.data.content || r.data, loaded: true })); })
      .catch(() => { if (alive) setState((s) => ({ ...s, loaded: true })); });
    /* the website builder's published settings (nav / logo / footer) — independent of the
       content request and failure-tolerant: without them the chrome keeps its built-in nav */
    axios.get(`${API}/site/settings`, { headers: { Authorization: undefined } })
      .then((r) => { if (alive && r && isObj(r.data)) setState((s) => ({ ...s, cms: r.data })); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const value = useMemo(() => state, [state]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** the raw published website-builder settings document (GET /api/site/settings), or null */
export function useSiteCmsSettings() {
  return useContext(Ctx).cms || null;
}

/** useSiteContent("kitchens", KITCHENS_DEFAULTS) → the CMS override merged over the page's defaults.
 *  In the visual editor the unsaved DRAFT is merged last, so an edit shows on the real page at once. */
export function useSiteContent(key, fallback) {
  const { content } = useContext(Ctx);
  const edit = useEditSession();
  const draft = edit && edit.active ? edit.draft : null;
  return useMemo(() => {
    if (!key) return mergeContent(content, draft || undefined);
    const saved = mergeContent(fallback, content ? content[key] : undefined);
    return draft && draft[key] !== undefined ? mergeContent(saved, draft[key]) : saved;
  }, [content, draft, key, fallback]);
}
